import moment from 'moment';
import AccountLib from '../Account';
import {
  Arr,
  join
} from '../Array.lib';
import {
  NOT
} from '../Boolean.lib';
import globalErrorHandler from '../Error/globalErrorHandler.fun';
import {
  Fun
} from '../Function.lib';
import {
  getPersonName,
  Obj
} from '../Object.lib';
import {
  COLLECTION__ACCOUNTS,
  readLoopbackRecord
} from '../services/BE/loopback.api';
import {
  trim
} from '../String.lib';
import prependPrivateNote from './prependPrivateNote.fun';
import updateCandidateManually from './updateCandidateManually.async';

/**
 * Updates a candidate's account assignment, optionally saving changes to DB,
 * logs previous account and additional emails in private notes, and triggers
 * a custom updater function.
 *
 * @async
 * @function driveCandidateAccountChange
 * @param {Object} params - Function parameters.
 * @param {Object} [params.candidate={}] - Candidate instance object.
 * @param {string|Object} [params.accountId={}] - New account ID reference.
 * @param {boolean} [params.saveToDB=false] - Whether to persist changes to database.
 * @param {Function} [params.updater=Fun] - Optional function to handle post-update logic, receives `update` object.
 * @returns {Promise<Object>} Updated candidate object with new account info and private notes.
 *
 * @description
 * - Reads the new account using `readLoopbackRecord`.
 * - Prepends candidate's existing `privateNotes` with:
 *   1. Previous additional emails from `candidate.emailsList`.
 *   2. Previous recruiter account info including firstName, lastName, company, and ID.
 * - Updates candidate object with new `accountId`, private notes, and account reference.
 * - Optionally persists changes to the database using `updateLoopbackRecord`.
 * - Cleans candidate cache if saving to DB.
 * - Executes the `updater` function with the `update` object.
 */
export default async function driveCandidateAccountChange({
  candidate = {},
  accountId = {},
  saveToDB = false,
  updater: renderUpdater = Fun
}) {

  try {

    const previousAccount = Obj(candidate.account);

    const account = Obj(
      await readLoopbackRecord({
        collection: COLLECTION__ACCOUNTS,
        where: { id: accountId },
        limit: 1,
        mapper: AccountLib.mapAccount
      })
    );

    if (NOT(account.id)) {
      throw new Error(`Account with ID ${accountId} not found.`);
    }

    let updatedPrivateNotes = trim(candidate.privateNotes);

    // Copy additional candidate emails (emailsList) to private notes and remove from emailsList.
    // "2025-08-26: Previous additional emails: name1, email1; name2, email2; name3,email3;"

    const emailsList = Arr(candidate.emailsList);

    if (emailsList.length) {
      updatedPrivateNotes = await prependPrivateNote({
        candidate,
        note: join([
          'Previous additional emails:',
          candidate.emailsList.map(({ name, email }) => join([name, email])).join('; ')
        ], ' ') + ';'
      });
      candidate.emailsList = [];
    }

    // Copy the origin account firstName, LastName, Company, id 
    // to the private notes before changing and record the date.
    // "2025-08-26: Previous recruiter account - Bob Bartruff, 10 By 10 - 68a8b4d991500bfde2f9d44d

    updatedPrivateNotes = await prependPrivateNote({
      candidate,
      note: join([
        'Previous recruiter account',
        join([
          getPersonName(previousAccount),
          previousAccount.companyName
        ]),
        previousAccount.id
      ], ' - ')
    });

    const update = {
      // Basic attributes (DB only)
      accountId,
      privateNotes: updatedPrivateNotes,
      emailsList: candidate.emailsList,
      manuallyUpdatedAt: moment().toISOString(),
      // Extended attributes (Memory only)
      account,
      recruiter: account
    };

    if (saveToDB) {
      await updateCandidateManually({
        id: candidate.id,
        update,
        key: 'driveCandidateAccountChange'
      });
    }

    Object.assign(candidate, update);

    renderUpdater(update);

  }
  catch (error) {

    globalErrorHandler(error);

  }

  return candidate;

}
